import { Component, Inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogModule } from "@angular/material/dialog";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { NgxPaginationModule } from "ngx-pagination";
import { TranslateModule } from "@ngx-translate/core";
import { ApiService } from "../../_services/api-data.service";
import { ToastService } from "../../_services/toast.service";
import { URLS } from "../../_config/api.config";

@Component({
  selector: "app-vat-history-dialog",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    NgxPaginationModule,
    TranslateModule,
  ],
  template: `<div class="dialog-header">
      <div>
        <h2 mat-dialog-title>{{ "vatHistory" | translate }}</h2>
        <p>Review changes to your VAT rate.</p>
      </div>
      <button
        type="button"
        class="icon-button"
        mat-dialog-close
        aria-label="Close dialog"
      >
        <span class="material-symbols-outlined">close</span>
      </button>
    </div>
    <mat-dialog-content class="dialog-content"
      ><div class="table-scroll" [attr.aria-busy]="loading">
        <table>
          <thead>
            <tr>
              <th>{{ "newVat" | translate }}</th>
              <th>{{ "oldVat" | translate }}</th>
              <th>{{ "vatCreateDate" | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="
                let vat of records
                  | paginate
                    : {
                        itemsPerPage: pageSize,
                        currentPage: page,
                        totalItems: total,
                      }
              "
            >
              <td>
                {{
                  vat.new_vat_percentage ? vat.new_vat_percentage + "%" : "-"
                }}
              </td>
              <td>
                {{
                  vat.old_vat_percentage ? vat.old_vat_percentage + "%" : "-"
                }}
              </td>
              <td>{{ vat.created_at | date: "MMM dd, yyyy" }}</td>
            </tr>
          </tbody>
        </table>
        <div class="state-message" *ngIf="!loading && !records.length">
          <span class="material-symbols-outlined">history_toggle_off</span
          >{{ "noDataFound" | translate }}
        </div>
        <div class="state-message" *ngIf="loading">
          <mat-spinner diameter="22"></mat-spinner>
        </div></div
    ></mat-dialog-content>
    <mat-dialog-actions class="dialog-footer" *ngIf="total > pageSize"
      ><pagination-controls
        class="paginator"
        previousLabel="‹"
        nextLabel="›"
        (pageChange)="changePage($event)"
      ></pagination-controls
    ></mat-dialog-actions>`,
  styles: [
    `
      :host {
        display: block;
        color: #253750;
        font-size: 12px;
      }
      .dialog-header {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        padding: 4px 24px 18px;
        border-bottom: 1px solid #edf1f6;
        align-items: center;
      }
      .dialog-header h2 {
        margin: 0;
        padding: 0;
        font-size: 17px;
        font-weight: 600;
        line-height: 24px;
      }
      .dialog-header p {
        margin: 5px 0 0;
        color: #7d8a9d;
        font-size: 12px;
      }
      .icon-button {
        display: grid;
        place-items: center;
        width: 28px;
        height: 28px;
        border: 0;
        border-radius: 7px;
        background: transparent;
        color: #7d8a9d;
        cursor: pointer;
      }
      .icon-button:hover {
        color: var(--primary-color);
        background: color-mix(in srgb, var(--primary-color) 6%, white);
      }
      .icon-button span {
        font-size: 20px;
      }
      .dialog-content {
        margin: 0;
        padding: 0;
      }
      .table-scroll {
        overflow: auto;
        min-height: 160px;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        text-align: start;
      }
      th {
        padding: 11px 18px;
        background: #fbfcfe;
        color: #7d8a9d;
        font-size: 10px;
        font-weight: 500;
        white-space: nowrap;
      }
      td {
        padding: 13px 18px;
        border-top: 1px solid #edf1f6;
        color: #53647a;
      }
      td:first-child {
        color: var(--primary-color);
        font-weight: 500;
      }
      .state-message {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 160px;
        color: #8491a3;
      }
      .state-message span {
        font-size: 23px;
      }
      .dialog-footer {
        justify-content: flex-end;
        min-height: 0;
        margin: 0;
        padding: 12px 18px;
        border-top: 1px solid #edf1f6;
      }
      .paginator {
        font-size: 11px;
      }
      @media (max-width: 550px) {
        .dialog-header {
          padding-inline: 16px;
        }
        th,
        td {
          padding-inline: 14px;
        }
      }
    `,
  ],
})
export class VatHistoryDialogComponent implements OnInit {
  records: any[] = [];
  total = 0;
  page = 1;
  readonly pageSize = 10;
  loading = false;
  constructor(
    @Inject(MAT_DIALOG_DATA) private data: { direction: boolean },
    private api: ApiService,
    private toast: ToastService,
  ) {}
  ngOnInit(): void {
    this.load();
  }
  changePage(page: number): void {
    this.page = page;
    this.load();
  }
  private load(): void {
    this.loading = true;
    this.api
      .get(URLS.getMyVatHistory, { page: this.page, limit: this.pageSize })
      .subscribe(
        (data) => {
          if (data?.code === 200) {
            this.records = data.data?.result || [];
            this.total = data.data?.total_count || 0;
          } else {
            this.records = [];
            this.total = 0;
            this.toast.displayToast(data?.message, null, "danger");
          }
          this.loading = false;
        },
        (error) => {
          this.records = [];
          this.total = 0;
          this.loading = false;
          this.toast.displayToast(error?.message, null, "danger");
        },
      );
  }
}
